/**
 * Responsive utilities for handling different screen sizes
 */

export type ScreenSize = 'mobile' | 'tablet' | 'desktop';

export interface ResponsiveConfig {
  mobileBreakpoint?: number;  // Default: 640px
  tabletBreakpoint?: number;  // Default: 1024px
}

const DEFAULT_CONFIG: Required<ResponsiveConfig> = {
  mobileBreakpoint: 640,
  tabletBreakpoint: 1024,
};

/**
 * Get current screen size based on window width
 */
export function getScreenSize(config?: ResponsiveConfig): ScreenSize {
  const { mobileBreakpoint, tabletBreakpoint } = { ...DEFAULT_CONFIG, ...config };
  const width = window.innerWidth;

  if (width < mobileBreakpoint) {
    return 'mobile';
  } else if (width < tabletBreakpoint) {
    return 'tablet';
  } else {
    return 'desktop';
  }
}

/**
 * Check if current screen is mobile
 */
export function isMobile(config?: ResponsiveConfig): boolean {
  return getScreenSize(config) === 'mobile';
}

/**
 * Check if current screen is tablet
 */
export function isTablet(config?: ResponsiveConfig): boolean {
  return getScreenSize(config) === 'tablet';
}

/**
 * Check if current screen is desktop
 */
export function isDesktop(config?: ResponsiveConfig): boolean {
  return getScreenSize(config) === 'desktop';
}

/**
 * Watch for screen size changes
 */
export function watchScreenSize(
  callback: (size: ScreenSize, previousSize: ScreenSize) => void,
  config?: ResponsiveConfig
): () => void {
  let previousSize = getScreenSize(config);

  const handleResize = () => {
    const currentSize = getScreenSize(config);
    if (currentSize !== previousSize) {
      callback(currentSize, previousSize);
      previousSize = currentSize;
    }
  };

  // Debounce resize events
  let resizeTimeout: NodeJS.Timeout;
  const debouncedResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(handleResize, 150);
  };

  window.addEventListener('resize', debouncedResize);

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', debouncedResize);
    clearTimeout(resizeTimeout);
  };
}

/**
 * Media query helper
 */
export function matchesMediaQuery(query: string): boolean {
  return window.matchMedia(query).matches;
}

/**
 * Watch media query changes
 */
export function watchMediaQuery(
  query: string,
  callback: (matches: boolean) => void
): () => void {
  const mediaQuery = window.matchMedia(query);

  // Initial callback
  callback(mediaQuery.matches);

  // Handler for changes
  const handler = (e: MediaQueryListEvent) => {
    callback(e.matches);
  };

  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }
  // Legacy browsers
  else if (mediaQuery.addListener) {
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }

  return () => {};
}